import { Injectable } from "angular";
import {
  LookupManagementConfig,
  LookupManagementServiceProvider,
} from "../lookupManagement";
import {
  RecordGridViewServiceProvider,
  vsProviderName as RecordGridViewServiceProviderName,
} from "../../views/recordGridView";
import {
  FormSubmitArgs,
  FormViewService,
  FormViewServiceProvider,
  vsProviderName as FormViewServiceProviderName,
  vsServiceName as FormViewServiceName,
} from "../../forms/formView";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../grids/columns/columnDefinitionService";
import { RecordDataSource } from "../../grids";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../server/serverUtility";
import fieldHelpers from "../../../ui-formly/fieldHelpers";

export interface SimpleLookupManagementConfig {
  lookupEditPermission: string | string[];
  namePlural?: string;
  nameSingular: string;
  url: string;
  objectName: string;
  lookupManagementServiceProviderName: string;
}

export function simpleLookupManagementConfig(
  config: SimpleLookupManagementConfig
): Injectable<(...args: any[]) => void> {
  return [
    config.lookupManagementServiceProviderName,
    RecordGridViewServiceProviderName,
    FormViewServiceProviderName,
    (
      lookupManagementServiceProvider: LookupManagementServiceProvider,
      recordGridViewServiceProvider: RecordGridViewServiceProvider,
      formViewServiceProvider: FormViewServiceProvider
    ) => {
      configureGrid(recordGridViewServiceProvider, config);
      configureForm(formViewServiceProvider, config);
      configureLookupManagement(lookupManagementServiceProvider, config);
    },
  ];
}

function configureGrid(
  recordGridViewServiceProvider: RecordGridViewServiceProvider,
  config: SimpleLookupManagementConfig
) {
  recordGridViewServiceProvider.addGridConfiguration(config.objectName, [
    ColumnDefinitionServiceName,
    ServerUtilityName,
    (
      columnDefinitionService: ColumnDefinitionService,
      serverUtility: ServerUtility
    ) => {
      return {
        columns: [
          columnDefinitionService.getNumberColDef({ field: "Id", hide: true }),
          columnDefinitionService.getStringColDef({
            field: "Name",
            header: config.nameSingular,
          }),
          columnDefinitionService.getBooleanColDef({
            field: "Disabled",
          }),
        ],
        gridDataSource: new RecordDataSource(serverUtility, config.url),
        defaultSort: "Name",
        namePlural: config.namePlural ?? `${config.nameSingular}s`,
        nameSingular: config.nameSingular,
        showAddButton: true,
        showAddButtonPermission: config.lookupEditPermission,
        showSelectButtons: false,
      };
    },
  ]);
}

function configureForm(
  formViewServiceProvider: FormViewServiceProvider,
  config: SimpleLookupManagementConfig
) {
  formViewServiceProvider.addFormViewConfig(config.objectName, [
    FormViewServiceName,
    (formViewService: FormViewService) => {
      return {
        fields: [
          fieldHelpers.simpleStringField({
            key: "Name",
            label: config.nameSingular,
            required: true,
          }),
          fieldHelpers.simpleBoolField({
            key: "Disabled",
            defaultValue: false,
          }),
        ],
        title: (model) =>
          (model.Id ? "Edit" : "Create") + " " + config.nameSingular,
        name: config.objectName,
        submitForm: (args: FormSubmitArgs) => {
          const model = args.model;
          return formViewService.saveForm({
            formObject: model,
            formViewController: args.formViewController,
            url: config.url,
          });
        },
      };
    },
  ]);
}

function configureLookupManagement(
  lookupManagementServiceProvider: LookupManagementServiceProvider,
  config: SimpleLookupManagementConfig
) {
  lookupManagementServiceProvider.addLookup(config.objectName, {
    displayName: config.nameSingular,
    config: [
      ServerUtilityName,
      (serverUtility: ServerUtility) => {
        return <LookupManagementConfig>{
          fetchFormModel(id) {
            return serverUtility.querySingle({
              url: config.url,
              query: { Id: id },
            });
          },
          baseUrl: config.url,
        };
      },
    ],
  });
}
